import {View} from 'react-native'
import {useLingui} from '@lingui/react/macro'

import {sanitizeHandle} from '#/lib/strings/handles'
import {
  type Classroom,
  type ClassroomMember,
  type MemberRole,
} from '#/state/classrooms'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import {Trash_Stroke2_Corner0_Rounded as TrashIcon} from '#/components/icons/Trash'
import {Text} from '#/components/Typography'

/** The order a classroom lists its people in: the school, then the room. */
export const ROLE_ORDER: Record<MemberRole, number> = {
  admin: 0,
  teacher: 1,
  student: 2,
  parent: 3,
}

export function sortMembers(
  classroom: Classroom,
  members: ClassroomMember[],
): ClassroomMember[] {
  return members
    .slice()
    .sort(
      (a, b) =>
        ROLE_ORDER[a.role] - ROLE_ORDER[b.role] ||
        a.joinedAt.localeCompare(b.joinedAt),
    )
}

export function MemberRow({
  member,
  isSchool,
  onRemove,
  removeLabel,
}: {
  member: ClassroomMember
  /** The school's own seat: it is the classroom, not one of its people. */
  isSchool?: boolean
  onRemove?: (did: string) => void
  removeLabel?: string
}) {
  const t = useTheme()
  const {t: l} = useLingui()
  const name = member.displayName || `@${member.handle}`

  // Translated here rather than in a helper: the message macro has to see the
  // call next to the `t` it came from.
  const roleLabel =
    member.role === 'admin'
      ? l`Admin`
      : member.isModerator
        ? l`Moderator`
        : member.role === 'teacher'
          ? l`Teacher`
          : member.role === 'student'
            ? l`Student`
            : l`Parent`

  return (
    <View
      testID={`classroomMember-${member.did}`}
      style={[a.flex_row, a.align_center, a.gap_md]}>
      <View
        style={[
          a.align_center,
          a.justify_center,
          a.rounded_full,
          t.atoms.bg_contrast_25,
          {width: 40, height: 40},
        ]}>
        <Text emoji style={[a.text_lg, a.font_semi_bold, t.atoms.text]}>
          {initialOf(name)}
        </Text>
      </View>
      <View style={[a.flex_1]}>
        <Text emoji numberOfLines={1} style={[a.text_md, a.font_semi_bold]}>
          {name}
        </Text>
        <Text
          numberOfLines={1}
          style={[a.text_sm, t.atoms.text_contrast_medium]}>
          {sanitizeHandle(member.handle, '@')}
          {member.placeholder ? ` · ${l`linked automatically`}` : ''}
        </Text>
      </View>
      <View
        style={[
          a.rounded_full,
          t.atoms.bg_contrast_25,
          a.px_sm,
          {paddingVertical: 2},
        ]}>
        <Text style={[a.text_xs, a.font_semi_bold, t.atoms.text_contrast_high]}>
          {roleLabel}
        </Text>
      </View>
      {onRemove && removeLabel && !isSchool && (
        <Button
          testID={`classroomRemoveMember-${member.did}`}
          label={removeLabel}
          onPress={() => onRemove(member.did)}
          color="secondary"
          size="small"
          shape="round">
          <ButtonIcon icon={TrashIcon} />
        </Button>
      )}
    </View>
  )
}

function initialOf(name: string): string {
  const cleaned = name.replace(/^@/, '').trim()
  return cleaned.slice(0, 1).toUpperCase() || '?'
}
